import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/admin/auth/useAuthStore';
import { adminAuthService } from '@/admin/api/adminAuth.service';
import { purgeLegacyAuthKeys } from '@/services/storage.service';

/**
 * Admin auth gate (M3 S3.1.1).
 * Loading skeleton while fetchMe in flight; anon → /admin/login?next=;
 * disabled → /admin/disabled; else <Outlet/>.
 */
export default function AdminAuthGuard() {
  const status = useAuthStore((s) => s.status);
  const admin = useAuthStore((s) => s.admin);
  const location = useLocation();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    if (status !== 'idle') return;
    attempted.current = true;
    // One-shot purge of legacy localStorage auth keys (memory-only session now).
    // NOTE: purge targets legacy keys only — the persisted admin refresh token
    // (`admin_refresh_token`) survives, so reloads / new tabs can re-authenticate.
    purgeLegacyAuthKeys();
    useAuthStore.setState({ status: 'loading' });
    (async () => {
      // Fresh boot with no access token: try silent refresh first (new tabs, reloads).
      if (!useAuthStore.getState().accessToken) {
        const token = await adminAuthService.refresh().catch(() => null);
        if (!token) {
          useAuthStore.setState({ status: 'anon' });
          return;
        }
      }
      await adminAuthService.fetchMe().catch(() => {
        // fetchMe sets anon on 401/403; other errors keep prior state.
        const cur = useAuthStore.getState().status;
        if (cur === 'loading' || cur === 'idle') {
          useAuthStore.setState({ status: 'anon' });
        }
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <span className="sr-only">Loading...</span>
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (status === 'disabled' || admin?.status === 'disabled') {
    return <Navigate to="/admin/disabled" replace />;
  }

  if (status === 'anon' || !admin) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admin/login?next=${next}`} replace state={{ from: location }} />;
  }

  return <Outlet />;
}
